import { Calendar, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface InlineEditDateProps {
  value: string | null;
  onSave: (value: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const InlineEditDate: React.FC<InlineEditDateProps> = ({
  value,
  onSave,
  disabled = false,
  placeholder = 'Not set',
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValue, setEditValue] = useState(value || '');

  const formatDate = (dateString: string | null) => {
    if (!dateString) return placeholder;
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSave = async () => {
    if (editValue === value || disabled) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  return (
    <Popover open={isEditing} onOpenChange={setIsEditing}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={`h-auto py-1 px-2 justify-start hover:bg-muted ${className}`}
          disabled={disabled || saving}
        >
          {saving ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Saving...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3 opacity-50" />
              <span className="text-sm">{formatDate(value)}</span>
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Select Date and Time</label>
            <Input
              type="datetime-local"
              value={editValue ? new Date(editValue).toISOString().slice(0, 16) : ''}
              onChange={(e) => setEditValue(e.target.value ? new Date(e.target.value).toISOString() : '')}
              disabled={saving}
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InlineEditDate;

